import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import { RiPlayCircleLine } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import useMissionCommand from '@/hooks/command/mavlink/useMissionCommand';
import EventEmitter from '@/libs/EventEmitter';
import { NotifierService as notifier } from '@/libs/Notifier';
import telemetryStore from '@/stores/telemetryStore';
import { getModeId, executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const dispatch = useDispatch();
  const [isProcessing, setProcessing] = useState(false);
  const actionCommand = useActionCommand();
  const missionCommand = useMissionCommand();

  //#region Process Functions
  const process = (robot) => {
    switch (robot.model.maker) {
      case 'ArduPilot':
        return processArduPilot(robot);
      case 'PX4':
        return processPX4(robot);
      default:
        return Promise.resolve();
    }
  };

  const processArduPilot = async (robot) => {
    try {
      await executeMavLinkCommand(robot, actionCommand.setMode(getModeId(robot.model.vehicle)));

      // ARM 상태가 아닌 경우
      if (!telemetryStore.get(robot.id, MESSAGE.HEARTBEAT).isArm) {
        await executeMavLinkCommand(robot, actionCommand.arm());
      }
      await executeMavLinkCommand(robot, missionCommand.startArduPilot());

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processPX4 = async (robot) => {
    try {
      // ARM 상태가 아닌 경우
      if (!telemetryStore.get(robot.id, MESSAGE.HEARTBEAT).isArm) {
        await executeMavLinkCommand(robot, actionCommand.arm());
      }
      await executeMavLinkCommand(robot, missionCommand.startPX4());

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  // 성공 시 처리
  const onSuccess = (robot) => {
    EventEmitter.publish(`${robot.id}/event/removeGoto`);

    notifier.success(`Mission started successfully on ${robot.name}.`);
    setProcessing(false);
  };

  // 실패 시 처리
  const onFailure = (robot) => {
    notifier.error(`Failed to start mission on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const doCommand = () => {
    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Begins autonomous execution of the uploaded mission</div>
      <ConfirmSlider
        width="100%"
        height={40}
        onConfirm={doCommand}
        isProcessing={isProcessing}
        labelStyle={{ fontSize: 12 }}
      />
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Start';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = false;
Command.commanderOnly = true;
Command.Icon = RiPlayCircleLine;
Command.Panel = Panel;

export default Command;
